import { create } from 'zustand';
import {
  journalService,
  type ApiJournalPlaybook,
  type ApiJournalPlaybookCreate,
  type ApiJournalPlaybookUpdate,
} from '../services/journal.service';
import { extractErrorMessage } from '../lib/api-errors';
import { withMutation } from './storeUtils';

/** Re-export ApiJournalPlaybook as ServerPlaybook for page component compatibility. */
export type { ApiJournalPlaybook as ServerPlaybook };

// --- Interfaces ---

interface PlaybookApiState {
  playbooks: ApiJournalPlaybook[];
  page: number;
  limit: number;
  total: number;
  hasMore: boolean;
  /** True while a list fetch is in flight. */
  isListLoading: boolean;
  /** True while a create / update / delete mutation is in flight. */
  isMutating: boolean;
  /** True after at least one successful fetchPlaybooks() call (#719 — skip redundant fetches). */
  hasFetched: boolean;
  error: string | null;
}

interface PlaybookApiActions {
  fetchPlaybooks: (page?: number) => Promise<void>;
  createPlaybook: (data: ApiJournalPlaybookCreate) => Promise<ApiJournalPlaybook>;
  updatePlaybookById: (id: string, data: ApiJournalPlaybookUpdate) => Promise<void>;
  deletePlaybookById: (id: string) => Promise<void>;
  clearError: () => void;
  /** Reset cached state on logout so a new session starts with a fresh fetch. */
  resetPlaybooks: () => void;
}

type PlaybookStore = PlaybookApiState & PlaybookApiActions;

// --- Store ---

export const usePlaybookStore = create<PlaybookStore>()((set, get) => ({
  // --- State ---
  playbooks: [],
  page: 1,
  limit: 20,
  total: 0,
  hasMore: false,
  isListLoading: false,
  isMutating: false,
  hasFetched: false,
  error: null,

  // --- Actions ---

  fetchPlaybooks: async (page = 1) => {
    set({ isListLoading: true, error: null });
    try {
      const result = await journalService.getPlaybooks({ page });
      set({
        playbooks: result.items,
        page: result.page,
        limit: result.limit,
        total: result.total,
        hasMore: result.hasMore,
        isListLoading: false,
        hasFetched: true,
      });
    } catch (error) {
      set({
        error: extractErrorMessage(error, 'Failed to fetch playbooks'),
        isListLoading: false,
      });
      // Rethrow so that mutating actions (createPlaybook, etc.) can surface
      // list-refresh failures to their callers rather than silently swallowing them.
      throw error;
    }
  },

  createPlaybook: async (data) => {
    const playbook = await withMutation(set, () => journalService.createPlaybook(data), 'Failed to create playbook');
    set({ isMutating: false });
    // Always reset to page 1 after a create: the user wants to see the
    // newly-created playbook at the top of the list, not remain on whatever
    // page was active before.
    await get().fetchPlaybooks(1);
    return playbook;
  },

  updatePlaybookById: async (id, data) => {
    await withMutation(set, () => journalService.updatePlaybook(id, data), 'Failed to update playbook');
    set({ isMutating: false });
    await get().fetchPlaybooks(get().page);
  },

  deletePlaybookById: async (id) => {
    await withMutation(set, () => journalService.deletePlaybook(id), 'Failed to delete playbook');
    // Optimisation: if there was exactly one item visible and we are not on
    // the first page, we know the page will be empty after the delete — skip
    // straight to the previous page rather than making a wasted round-trip.
    const { page: currentPage, playbooks } = get();
    const targetPage =
      playbooks.length === 1 && currentPage > 1 ? currentPage - 1 : currentPage;
    set({ isMutating: false });
    await get().fetchPlaybooks(targetPage);
  },

  clearError: () => set({ error: null }),

  resetPlaybooks: () =>
    set({
      playbooks: [],
      page: 1,
      total: 0,
      hasMore: false,
      isListLoading: false,
      isMutating: false,
      hasFetched: false,
      error: null,
    }),
}));
